import { useMutation } from '@apollo/client';

import { UPDATE_USER_MUTATION } from 'Account/withUpdateUser';
import { countryRequiresState, USER_LOCATION_QUERY } from './useUserLocation';

export type UserLocationInput = {
  country: string;
  state: string | null;
};

type SaveUserLocationArgs = {
  country: string;
  state?: string | null;
};

/**
 * Saves a user's place of residence, mapping a country's province or region
 * onto `state`.
 *
 * The saved location is written straight into the `USER_LOCATION_QUERY` entry
 * so every `useUserLocation` consumer sees `hasValidLocationData` flip without
 * a refetch.
 */
export const useSaveUserLocation = (userId?: string) => {
  const [updateUser, { loading: saving }] = useMutation(UPDATE_USER_MUTATION);

  const saveLocation = async ({ country, state }: SaveUserLocationArgs) => {
    const userInput: UserLocationInput = {
      country,
      state: countryRequiresState(country) ? (state ?? null) : null,
    };

    await updateUser({
      variables: { userInput },
      update: cache => {
        cache.writeQuery({
          query: USER_LOCATION_QUERY,
          data: {
            user: {
              __typename: 'User',
              id: userId,
              country: {
                __typename: 'UserAttribute',
                value: userInput.country,
              },
              state: {
                __typename: 'UserAttribute',
                value: userInput.state,
              },
            },
          },
        });
      },
    });

    return userInput;
  };

  return { saveLocation, saving };
};
